import React, { useEffect, useState } from 'react';
import { Box } from '@mui/material';
import { createPortal } from 'react-dom';
import { usePageRenderContext } from '../browser/pagerender/pagerendercontext';
import { ZindexUtils } from '../../utils/zindexutils';

/**
 * Tamanhos padrão do modal.
 * 'as const' permite inferir tipos literais (número | string)
 */
const modalSizes = {
  sm: { w: 300, h: 400 },
  md: { w: 600, h: 500 },
  lg: { w: 900, h: 600 },
  xl: { w: 1140, h: 700 },
  r: { w: '100%', h: '100%' },
  full: { w: '100%', h: '100%' },
  dialog: { w: 400, h: 200 }
} as const;

type ModalSize = keyof typeof modalSizes;

/** Tipagem explícita do retorno do hook. Ajuste se tiver tipagem própria. */
interface PageRenderContext {
  paginaID?: string | undefined;
}

/** Props do componente */
interface EasyModalProps {
  open: boolean;
  onClose: () => void;
  children: React.ReactNode;
  size?: ModalSize;
  paginaIDEx?: string;
}

/** Componente EasyModal tipado */
const EasyModal: React.FC<EasyModalProps> = ({
  open,
  onClose,
  children,
  size = 'lg',
  paginaIDEx
}) => {
  // forçar tipagem do hook (substitua se tiver tipo exportado)
  const { paginaID } = (usePageRenderContext() as PageRenderContext) ?? {};
  const [container, setContainer] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const id = paginaID ?? paginaIDEx ?? '';
    const el = id ? document.getElementById(id) : null;
    setContainer(el || null);
  }, [paginaID, paginaIDEx]);

  // se não está aberto ou não encontrou o container alvo, não renderiza
  if (!open || !container) return null;

  const z: number = ZindexUtils.getMaxZIndex?.() ?? 1000;
  const baseSize = modalSizes[size];

  const handleBackdropKeyDown = (ev: React.KeyboardEvent) => {
    if (ev.key === 'Escape') {
      ev.stopPropagation();
      onClose();
    }
  };

  return createPortal(
    <Box
      role="dialog"
      aria-modal="true"
      sx={{
        position: 'absolute',
        inset: 0, // top:0 right:0 bottom:0 left:0
        bgcolor: 'rgba(0,0,0,0.5)',
        zIndex: z,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        p: { xs: 1, sm: 2 }, // padding responsivo para telas pequenas
        overflow: 'hidden' // evita que backdrop gere scroll dentro do container pai
      }}
      onClick={onClose}
      onKeyDown={handleBackdropKeyDown}
      tabIndex={-1}
    >
      <Box
        onClick={(e) => e.stopPropagation()}
        sx={{
          backgroundColor: 'white',
          borderRadius: 2,
          boxShadow: 6,
          zIndex: z + 1,

          display: 'flex',
          flexDirection: 'column',

          // largura/altura responsivas: sempre 100% até um max definido
          width: '100%',
          maxWidth: typeof baseSize.w === 'number' ? `${baseSize.w}px` : baseSize.w,
          height: '100%',
          maxHeight: typeof baseSize.h === 'number' ? `${baseSize.h}px` : baseSize.h,

          // garante que elementos internos em flex/grid não "estoure" o container
          minWidth: 0,
          minHeight: 0,

          // evita que conteúdo saia do modal; o conteúdo interno será rolável
          overflow: 'hidden',

          // força que o primeiro filho ocupe o espaço e gere scroll quando necessário
          '& > *': {
            flex: 1,
            minHeight: 0,
            overflow: 'auto'
          }
        }}
        data-testid="easy-modal-root"
      >
        {children}
      </Box>
    </Box>,
    container
  );
};

export default EasyModal;
